/* The questions people ask before buying, answered on the page they ask them
 * on.
 *
 * This exists for two audiences at once. A buyer gets the answer without
 * leaving for Discord or the Terms page, which is where a sale goes to die.
 * And a search engine -- increasingly an answer engine quoting a page rather
 * than linking it -- gets a question-and-answer block it can lift directly.
 * The competing sites that outrank us all carry one.
 *
 * Every answer here restates something already true elsewhere on this site:
 * the plans on the pricing panel, the refund terms in Terms of Service, the
 * hardware id described in the Privacy Policy, the loader on the Downloads
 * page. Nothing is promised here that is not promised there -- an FAQ is the
 * easiest place in a site to quietly invent a guarantee, and a claim with
 * schema markup around it is one a search engine will happily repeat.
 */

export interface FaqEntry {
  question: string;
  answer: string;
}

export function productFaq(productName: string): FaqEntry[] {
  return [
    {
      question: `What do I get with ${productName}?`,
      answer:
        `Every module listed on this page, plus the updates released while your access is active. ` +
        `Access is activated as soon as your payment clears — there is no manual step and nothing to wait for.`,
    },
    {
      question: "How do I install and run it?",
      answer:
        "Sign in and open the Downloads page, then run the Veyra loader. It signs you in, fetches the current build " +
        "for the product you own and starts it. Your account needs a verified email address first, which is the link " +
        "sent to you when you sign up.",
    },
    {
      question: "How long does my access last?",
      answer:
        "For as long as the plan you bought: a week or a month. The time remaining on each " +
        "product is shown on your profile page.",
    },
    {
      question: "Can I use it on more than one computer?",
      answer:
        "A licence is tied to one machine. The hardware identifier of the computer you activate on is recorded so a " +
        "single licence cannot be shared between people. If your hardware changes, ask support to reset it.",
    },
    {
      question: "Can I get a refund?",
      answer:
        "Purchases are final, with one exception: if your hardware cannot run the software at all — it cannot read " +
        "the game's memory or do what it advertises — you are eligible for a refund. Contact support before doing " +
        "anything else. Chargebacks are prohibited and result in a permanent ban, so talking to us first is always " +
        "the better move. The full terms are on the Terms of Service page.",
    },
    {
      question: "Which games does Veyra support?",
      answer:
        "Deadlock, CS2, FiveM, Roblox, Apex Legends and Rust. Some are fully released and some are in open beta or " +
        "still in development, which is marked on each product and on the products page.",
    },
    {
      question: "Where do I get help?",
      answer:
        "On our Discord, linked in the footer of every page. That is where support actually happens, and it is the " +
        "fastest way to reach somebody.",
    },
  ];
}
